// ============================================================================
// logo-tap-fix.js — tap a piece of the logo to flip it see-through / solid (WD 483)
// ============================================================================
// Ryan 2026-10-31, on DB Logo: *"the 'Solid' one still removes some pieces from the logo interior ...
// shouldn't the Solid remove none from the interior?"* - and Mojoe's, whose dark field around the badge
// no rule can tell from a designed rectangle. Both are a question only the buyer can answer, so the
// buyer answers it by tapping the piece.
//
// ⛔ WHY A TAP AND NOT A RULE: "keep everything inside the logo" was measured over DB, Tigers, Carroll
// and Grand Hyatt by closing the ink at four radii. Every radius that closes DB's open gaps also fills
// between CARROLL's letters and GRAND HYATT's spacing. Asked of ONE region the buyer pointed at, the
// same closing is safe: it can only ever change the area under their finger.
//
// Pure functions over ImageData-shaped objects ({width, height, data}); node-testable.
//   clearRegionAt(cur, x, y)        - tap on ink: that colour's connected area goes see-through.
//   fillRegionAt(cur, src, x, y)    - tap on a see-through area: the smallest gap-closing that walls
//                                     the tapped area off from the outside decides its extent; the
//                                     pixels come back from `src` (the art before any key).
// Each returns { data, count, ... } (a NEW pixel array) or null when nothing could change.
// ============================================================================

const OPAQUE = 128;

/** RGB distance under which a pixel belongs to the tapped colour (JPEG noise + anti-alias). */
export const TAP_COLOR_TOL = 56;
/** The largest gap (px, on a 1024-px image) a fill may close to wall an area off. */
export const TAP_GAP_MAX_FRAC = 0.03;

function idx(w, x, y) { return (y * w + x); }

/**
 * Tap on ink: everything 4-connected to the tapped pixel within `tol` of its colour goes clear, plus the
 * one-pixel anti-aliased rim around it that sits between that colour and what it touches.
 */
export function clearRegionAt(cur, x, y, { tol = TAP_COLOR_TOL } = {}) {
  const { width: W, height: H, data } = cur;
  x = Math.round(x); y = Math.round(y);
  if (x < 0 || y < 0 || x >= W || y >= H) return null;
  const i0 = idx(W, x, y) * 4;
  if (data[i0 + 3] < OPAQUE) return null;
  const r0 = data[i0], g0 = data[i0 + 1], b0 = data[i0 + 2];
  const near = (p, t) => {
    const dr = data[p] - r0, dg = data[p + 1] - g0, db = data[p + 2] - b0;
    return data[p + 3] >= OPAQUE && dr * dr + dg * dg + db * db <= t * t;
  };
  const N = W * H;
  const mark = new Uint8Array(N);
  const stack = [idx(W, x, y)];
  mark[stack[0]] = 1;
  let count = 0;
  while (stack.length) {
    const k = stack.pop();
    count += 1;
    const cx = k % W, cy = (k - cx) / W;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const n = idx(W, nx, ny);
      if (mark[n] || !near(n * 4, tol)) continue;
      mark[n] = 1;
      stack.push(n);
    }
  }
  const out = new Uint8ClampedArray(data);
  for (let k = 0; k < N; k++) if (mark[k]) out[k * 4 + 3] = 0;
  /* THE RIM (WD 485, Ryan 11-01: "you can still always see white remnants in the outline of the shape removed").
     The edge between the tapped colour C and its neighbour N is a band of BLENDED pixels - 2-3 px wide on an
     upscaled or JPEG logo, not the 1 px the first cut cleared - so a white piece left a pale outline on its
     neighbour. Each pixel within RIM px of the cleared area is un-mixed: t = how much of it is C (its position on
     the C -> N line); it keeps N's colour at (1 - t) of its alpha. A pure-C pixel goes clear, a pure-N pixel is
     untouched, and the neighbour ends with a clean anti-aliased edge onto see-through instead of a halo.
     N = the colour farthest from C among the solid, un-cleared pixels just beyond it; a pixel that is not on that
     line (a third colour) is left alone. With no solid N in reach (C against see-through), the pixel is C's own
     blended edge and goes clear within 2 px. */
  const RIM = 3;
  const toCut = chessDist(mark, W, H, 1);
  const tol2 = tol * tol;
  const C = [r0, g0, b0];
  for (let k = 0; k < N; k++) {
    if (mark[k] || data[k * 4 + 3] === 0 || toCut[k] > RIM) continue;
    const p4 = k * 4;
    const cx = k % W, cy = (k - cx) / W;
    let best = -1, bestD = tol2;
    for (let yy = Math.max(0, cy - RIM - 1); yy <= Math.min(H - 1, cy + RIM + 1); yy++) {
      for (let xx = Math.max(0, cx - RIM - 1); xx <= Math.min(W - 1, cx + RIM + 1); xx++) {
        const n = yy * W + xx;
        if (mark[n] || toCut[n] < toCut[k] || data[n * 4 + 3] < 250) continue; // same ring counts: a 1-px outline finds itself
        const q = n * 4, dr = data[q] - r0, dg = data[q + 1] - g0, db = data[q + 2] - b0;
        const dd = dr * dr + dg * dg + db * db;
        if (dd > bestD) { bestD = dd; best = q; }
      }
    }
    if (best < 0) {
      // nothing solid of another colour beside it: it is the cut piece's own edge, blended with a background that
      // was removed earlier (Carroll's white letters: grey at alpha ~200 where white met the black field) - clear it
      if (toCut[k] <= 2) out[p4 + 3] = 0;
      continue;
    }
    const Nc = [data[best], data[best + 1], data[best + 2]];
    let dot = 0, len2 = 0, res = 0;
    for (let c = 0; c < 3; c++) { dot += (data[p4 + c] - Nc[c]) * (C[c] - Nc[c]); len2 += (C[c] - Nc[c]) ** 2; }
    const t = Math.max(0, Math.min(1, dot / len2));
    for (let c = 0; c < 3; c++) { const e = data[p4 + c] - (Nc[c] + t * (C[c] - Nc[c])); res += e * e; }
    if (res > tol2) continue; // not a C/N blend - a third colour; leave it
    const a = Math.round(data[p4 + 3] * (1 - t));
    if (a < 24) { out[p4 + 3] = 0; continue; }
    out[p4] = Nc[0]; out[p4 + 1] = Nc[1]; out[p4 + 2] = Nc[2]; out[p4 + 3] = a;
  }
  /* ...and the HALF-CLEAR leftovers it touches (Mojoe's: an earlier key left the frame's outer column at alpha
     ~84 - not ink, so the flood stopped at it, and it stayed as a faint line down each side of the plate). Any
     partly-transparent pixel chained to the cleared area goes fully clear; solid ink is never touched here. */
  const chain = [];
  for (let k = 0; k < N; k++) if (mark[k]) chain.push(k);
  while (chain.length) {
    const k = chain.pop();
    const cx = k % W, cy = (k - cx) / W;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const n = idx(W, nx, ny);
      // ORIGINAL alpha: a rim pixel the un-mix just thinned is the neighbour's new edge, not a leftover to chain through
      const a = data[n * 4 + 3];
      if (a === 0 || a >= OPAQUE || out[n * 4 + 3] !== a) continue;
      out[n * 4 + 3] = 0;
      chain.push(n);
    }
  }
  return { data: out, count, width: W, height: H };
}

/** Chessboard distance from every pixel to the nearest pixel where `mask` is `want` (two passes, O(N)). */
function chessDist(mask, W, H, want) {
  const BIG = 1e6;
  const d = new Float32Array(W * H);
  for (let k = 0; k < W * H; k++) d[k] = mask[k] === want ? 0 : BIG;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const k = y * W + x; if (!d[k]) continue;
    let v = d[k];
    if (x > 0) v = Math.min(v, d[k - 1] + 1);
    if (y > 0) { v = Math.min(v, d[k - W] + 1); if (x > 0) v = Math.min(v, d[k - W - 1] + 1); if (x < W - 1) v = Math.min(v, d[k - W + 1] + 1); }
    d[k] = v;
  }
  for (let y = H - 1; y >= 0; y--) for (let x = W - 1; x >= 0; x--) {
    const k = y * W + x; if (!d[k]) continue;
    let v = d[k];
    if (x < W - 1) v = Math.min(v, d[k + 1] + 1);
    if (y < H - 1) { v = Math.min(v, d[k + W] + 1); if (x < W - 1) v = Math.min(v, d[k + W + 1] + 1); if (x > 0) v = Math.min(v, d[k + W - 1] + 1); }
    d[k] = v;
  }
  return d;
}

/** EXACT Euclidean distance from every pixel to the nearest pixel where `mask` is `want` (Felzenszwalb &
 *  Huttenlocher's two 1-D passes, O(N)). WD 485 (queue 4d): the fill's closing and grow-back use this - a ROUND brush -
 *  because the chessboard (square) one left a straight, axis-aligned edge where a fill closed a gap (DB Logo's notch). */
function euclidDist(mask, W, H, want) {
  const INF = 1e20;
  const f = new Float64Array(Math.max(W, H)), d = new Float64Array(Math.max(W, H));
  const v = new Int32Array(Math.max(W, H)), z = new Float64Array(Math.max(W, H) + 1);
  const g = new Float64Array(W * H);
  const pass = (n) => {
    let k = 0; v[0] = 0; z[0] = -INF; z[1] = INF;
    for (let q = 1; q < n; q++) {
      let sx = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
      while (sx <= z[k]) { k--; sx = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
      k++; v[k] = q; z[k] = sx; z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q++) { while (z[k + 1] < q) k++; d[q] = (q - v[k]) * (q - v[k]) + f[v[k]]; }
  };
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H; y++) f[y] = mask[y * W + x] === want ? 0 : INF;
    pass(H);
    for (let y = 0; y < H; y++) g[y * W + x] = d[y];
  }
  const out = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) f[x] = g[y * W + x];
    pass(W);
    for (let x = 0; x < W; x++) out[y * W + x] = Math.sqrt(d[x]);
  }
  return out;
}

/** The ink closed by r: dilate then erode, both from distance maps (`dist` picks the brush: chessboard = square,
 *  `euclidDist` = round). The image edge counts as NOT ink, so a closing never glues the mark to the frame. */
function closeBy(ink, W, H, r, toInk, dist = chessDist) {
  if (!r) return ink;
  const dil = new Uint8Array(W * H);
  for (let k = 0; k < W * H; k++) dil[k] = toInk[k] <= r ? 1 : 0;
  const toOut = dist(dil, W, H, 0);
  const out = new Uint8Array(W * H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const k = y * W + x;
    const edge = Math.min(x + 1, y + 1, W - x, H - y); // distance to beyond the frame
    out[k] = Math.min(toOut[k], edge) > r ? 1 : 0;
  }
  return out;
}

/** The connected clear area (4-conn over mask==0) holding k, and whether it reaches the image edge. */
function areaAt(mask, W, H, k) {
  const seen = new Uint8Array(W * H);
  const stack = [k];
  seen[k] = 1;
  let edge = false;
  const list = [];
  while (stack.length) {
    const c = stack.pop();
    list.push(c);
    const cx = c % W, cy = (c - cx) / W;
    if (cx === 0 || cy === 0 || cx === W - 1 || cy === H - 1) edge = true;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = cx + dx, ny = cy + dy;
      if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
      const n = ny * W + nx;
      if (seen[n] || mask[n]) continue;
      seen[n] = 1;
      stack.push(n);
    }
  }
  return { list, edge };
}

/**
 * Tap on a see-through area: close the ink's gaps one pixel at a time until the tapped area no longer
 * reaches the image edge (r = 0 is a fully enclosed pocket). That area, grown back by r so the closed
 * mouths fill too, is restored from `src`. Null when no gap up to `TAP_GAP_MAX_FRAC` walls it off - the
 * tap was on the real outside, and filling it would paint the background in.
 */
export function fillRegionAt(cur, src, x, y, { gapMaxFrac = TAP_GAP_MAX_FRAC, workMax = 384 } = {}) {
  const { width: W, height: H, data } = cur;
  x = Math.round(x); y = Math.round(y);
  if (x < 0 || y < 0 || x >= W || y >= H) return null;
  if (!src || src.width !== W || src.height !== H) return null;
  if (data[(y * W + x) * 4 + 3] >= OPAQUE) return null;
  // the search runs on a working copy (nearest-neighbour) so a 1024-px logo answers a tap at once
  const s = Math.min(1, workMax / Math.max(W, H));
  const w = Math.max(1, Math.round(W * s)), h = Math.max(1, Math.round(H * s));
  const ink = new Uint8Array(w * h);
  for (let yy = 0; yy < h; yy++) for (let xx = 0; xx < w; xx++) {
    const sx = Math.min(W - 1, Math.floor(xx / s)), sy = Math.min(H - 1, Math.floor(yy / s));
    ink[yy * w + xx] = data[(sy * W + sx) * 4 + 3] >= OPAQUE ? 1 : 0;
  }
  const wx = Math.min(w - 1, Math.floor(x * s)), wy = Math.min(h - 1, Math.floor(y * s));
  const k0 = wy * w + wx;
  if (ink[k0]) return null;
  const rMax = Math.max(1, Math.round(gapMaxFrac * Math.max(w, h)));
  const toInk = euclidDist(ink, w, h, 1); // round brush (WD 485, 4d)
  let found = null;
  for (let r = 0; r <= rMax && !found; r++) {
    const m = closeBy(ink, w, h, r, toInk, euclidDist);
    if (m[k0]) break; // the closing swallowed the tap point itself: no wall short of filling it
    const a = areaAt(m, w, h, k0);
    if (!a.edge) found = { list: a.list, r };
  }
  if (!found) return null;
  // grow the area back by r (the mouths it closed) and map it to full size
  const area = new Uint8Array(w * h);
  for (const k of found.list) area[k] = 1;
  const toArea = found.r ? euclidDist(area, w, h, 1) : null;
  const pad = Math.ceil(1 / s); // nearest-neighbour blocks: let the full-size edge reach the ink
  const out = new Uint8ClampedArray(data);
  const sd = src.data;
  let count = 0;
  for (let Y = 0; Y < H; Y++) for (let X = 0; X < W; X++) {
    const k = Y * W + X;
    if (data[k * 4 + 3] >= OPAQUE || sd[k * 4 + 3] < OPAQUE) continue;
    let inside = false;
    for (let dy = -pad + 1; dy < pad && !inside; dy++) for (let dx = -pad + 1; dx < pad && !inside; dx++) {
      const ax = Math.min(w - 1, Math.max(0, Math.floor((X + dx) * s))), ay = Math.min(h - 1, Math.max(0, Math.floor((Y + dy) * s)));
      const ak = ay * w + ax;
      inside = toArea ? toArea[ak] <= found.r : area[ak] === 1;
    }
    if (!inside) continue;
    out[k * 4] = sd[k * 4]; out[k * 4 + 1] = sd[k * 4 + 1]; out[k * 4 + 2] = sd[k * 4 + 2]; out[k * 4 + 3] = sd[k * 4 + 3];
    count += 1;
  }
  return count ? { data: out, count, gap: Math.round(found.r / s), width: W, height: H } : null;
}

/**
 * WD 485 (queue 4b): a chooser flip AFTER tap edits keeps the edits. `base` is the logo before any tap edit, `edited`
 * is it with the edits, `next` is the chooser's other version. Every pixel the edits changed comes from `edited`;
 * every pixel they did not touch comes from `next` - so a piece the buyer cleared stays clear, a pocket they filled
 * stays filled, and the pockets they never touched follow the chooser.
 * Returns a NEW pixel array, or null when the three are not the same size (not aligned - the caller keeps the old
 * behaviour: the chooser's version replaces the art).
 */
export function carryTapEdits(base, edited, next) {
  const W = next.width, H = next.height;
  if (base.width !== W || base.height !== H || edited.width !== W || edited.height !== H) return null;
  const b = base.data, e = edited.data, out = new Uint8ClampedArray(next.data);
  let carried = 0;
  for (let k = 0; k < out.length; k += 4) {
    const changed = Math.abs(e[k + 3] - b[k + 3]) > 2
      || (e[k + 3] > 0 && (Math.abs(e[k] - b[k]) > 2 || Math.abs(e[k + 1] - b[k + 1]) > 2 || Math.abs(e[k + 2] - b[k + 2]) > 2));
    if (!changed) continue;
    out[k] = e[k]; out[k + 1] = e[k + 1]; out[k + 2] = e[k + 2]; out[k + 3] = e[k + 3];
    carried++;
  }
  return { data: out, carried, width: W, height: H };
}
